<template>
  <div class="user-add">
    <b-container>
      <b-row>
        <b-col>
          <div class="floating-panel">
            <b-progress
                    :value="progress"
                    height="4px"
                    variant="primary"
            ></b-progress>
            <vue-element-loading :active="loading"></vue-element-loading>
            <r-form
                    :disabled="false"
                    :validators="validators[1]"
                    @submit="onStepOne"
                    key="one"
                    title="Enter the user's email address"
                    v-model="forms[1]"
                    v-show="step === 1"
            >
              <b-alert
                      show
                      v-if="userNotAvailable"
                      variant="danger"
              >
                User could not be added at this time.
              </b-alert>
              <template
                      slot="buttons"
                      slot-scope="{ form, valid }"
              >
                <b-button
                        :to="{name: 'users'}"
                        variant="outline-secondary"
                >
                  Cancel
                </b-button>
                <b-button
                        :disabled="valid"
                        @click="onStepOne(form)"
                        class="btn-arrow"
                        variant="primary"
                >
                  Next
                  <font-awesome-icon :icon="['far', 'arrow-right']"></font-awesome-icon>
                </b-button>
              </template>
            </r-form>
            <r-form
                    :disabled="userFound"
                    :validators="validators[2]"
                    @submit="onStepTwo"
                    key="two"
                    title="User details"
                    v-model="forms[2]"
                    v-show="step === 2"
            >
              <b-alert
                      show
                      v-if="userFound"
                      variant="success"
              >
                User has been found on the system.
                Please confirm this is the correct user and
                then press next to add them to your team.
              </b-alert>
              <template
                      slot="buttons"
                      slot-scope="{ form, valid }"
              >
                <b-button
                        @click="step = 1"
                        variant="outline-secondary"
                >
                  Back
                </b-button>
                <b-button
                        :disabled="valid"
                        @click="onStepTwo"
                        class="btn-arrow"
                        variant="primary"
                >
                  Next
                  <font-awesome-icon :icon="['far', 'arrow-right']"></font-awesome-icon>
                </b-button>
              </template>
            </r-form>
            <div v-show="step === 3">
              <div class="form-header">
                Select a team to add this user to
              </div>
              <div class="form-body">
                <multiselect
                        :options="data.teams"
                        deselectLabel=""
                        label="team"
                        placeholder=""
                        selectLabel=""
                        tagPlaceholder=" "
                        track-by="teamId"
                        v-model="selectedTeam"
                >
                    <template #caret="{ toggle }">
                      <caret-icon @mousedown.prevent.stop="toggle"></caret-icon>
                    </template>
                </multiselect>
              </div>
              <div class="form-footer">
                <b-button
                        @click="step = 2"
                        variant="outline-secondary"
                >
                  Back
                </b-button>
                <b-button
                        :disabled="selectedTeam === null"
                        @click="onStepThree"
                        class="btn-arrow"
                        variant="primary"
                >
                  Add to team
                  <font-awesome-icon :icon="['far', 'arrow-right']"></font-awesome-icon>
                </b-button>
              </div>
            </div>
            <div v-show="step === 4">
              <div class="form-body text-center p-5">
                <h2 class="title success green">
                  User successfully added
                </h2>
                <font-awesome-icon
                        :icon="['fal', 'check']"
                        class="green h1"
                        size="lg"
                ></font-awesome-icon>
                <div
                        :class="[
                          'mt-4',
                          'ml-auto',
                          'mr-auto',
                          'text-center',
                        ]"
                >
                  <b-button
                          :to="{ name: 'actions' }"
                          variant="primary"
                  >Actions</b-button>
                  <b-button
                          :class="[
                            'ml-2',
                          ]"
                          :to="{ name: 'users' }"
                          exact
                          variant="primary"
                  >User Management</b-button>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import User from '@/services/User/User';
import { forEach } from 'lodash';
import { email, required } from 'vuelidate/lib/validators';
import { form as UserForm, validators as UserValidators } from '@/services/User/UserForm';

export default {
  name: 'Users-Add',
  data() {
    return {
      forms: {
        1: {
          email: {
            label: 'Email',
            type: 'email',
            value: '',
          },
        },
        2: UserForm(),
      },
      validators: {
        1: {
          email: {
            required,
            email,
          },
        },
        2: UserValidators,
      },
      step: 1,
      loading: false,
      userNotAvailable: false,
      userFound: false,
      selectedTeam: null,

      data: {
        email: '',
        user: {},
        teams: [],
        teamId: null,
      },
    };
  },
  computed: {
    progress() {
      return (100 / 4) * this.step;
    },
  },
  created() {
    this.forms[2].email.disabled = true;
    delete this.forms[2].teams;
  },
  watch: {
    // Reset
    step(newStep) {
      if (newStep === 1) {
        this.resetForm(2);

        this.userFound = false;
      }
    },
  },
  methods: {
    async onStepOne(fields) {
      this.userNotAvailable = false;
      const { value } = fields.email;

      if (!value) {
        return false;
      }

      this.loading = true;

      const { data } = await User.searchByEmail(value);

      const { result, user } = data;

      // User not available
      if (!result) {
        this.userNotAvailable = true;
        this.loading = false;

        return false;
      }

      const form = this.forms[2];
      form.active.value = 1;
      form.email.value = value;

      // Found a user
      if (user.userId) {
        forEach(user, (val, key) => {
          if (key in form) {
            this.$set(form[key], 'value', val);
          }
        });

        this.userFound = user;
      }

      this.step = 2;
      this.loading = false;

      return true;
    },
    async onStepTwo() {
      this.loading = true;

      // Search for teams
      const { data } = await User.getManagedTeams();
      const { teams } = data;

      // We found more than one team, let the user select which team to add the user to
      if (teams.length > 1) {
        const teamOpts = [];

        teams.forEach((item) => {
          teamOpts.push({
            team: item.team,
            teamId: item.teamId,
          });
        });

        this.data.teams = teamOpts;

        this.step = 3;
        this.loading = false;

        return;
      }

      if (1 === teams.length) {
        this.data.teamId = teams[0].teamId;
      }

      this.addUser();
    },

    async onStepThree() {
      this.loading = true;

      const { teamId } = this.selectedTeam;
      this.data.teamId = teamId;

      this.addUser();
    },

    async addUser() {
      this.loading = true;
      // User already exists
      if (this.userFound) {
        this.addUserToTeam();

        return;
      }

      const form = this.forms[2];

      const params = {};
      forEach(form, (object, field) => {
        params[field] = object.value;
      });

      params.teamId = this.data.teamId;

      try {
        await User.store(params);

        this.step = 4;
        this.resetForm(1);
        this.resetForm(2);
      } finally {
        this.loading = false;
      }
    },

    async addUserToTeam() {
      this.loading = true;

      try {
        await User.update(this.userFound.userId, { teamId: this.data.teamId });

        this.step = 4;
        this.resetForm(1);
        this.resetForm(2);
      } finally {
        this.loading = false;
      }
    },
    resetForm(index) {
      forEach(this.forms[index], (object, key) => {
        if (key in this.forms[index]) {
          this.$set(this.forms[index][key], 'value', '');
        }
      });
    },
  },
};
</script>

<style lang="scss">
  .usersAdd {
    background: $gray;
  }

  .user-add {
    margin-top: 40px;
  }

  .floating-panel {
    box-shadow: -4px 5px 6px -3px rgba(0, 0, 0, 0.16);
    border-radius: 3px;
    background: white;
  }
</style>
